import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import InfoSection, {
  InfoSectionTitle,
} from '@/components/Approval/@components/InfoSection';
import {
  ApprovalFormTable,
  ApprovalTableUnit,
} from '@/components/Approval/@components/ApprovalFormTable';
import type { StoreOpenInputKey } from '@/utils/approval/storeOpen';
import { SectionHeader, StoreOpenRoot } from './styles';

export { default as StoreOpenInteriorDetailDialog } from './InteriorDetailDialog';

export type StoreOpenCellKey =
  | StoreOpenInputKey
  | 'brand'
  | 'shopName'
  | 'openDate'
  | 'shopArea'
  | 'shopForm'
  | 'constructionType'
  | 'interiorCompany'
  | 'interiorCost';

/** 섹션이 그리는 칸 전부 — 레거시는 2·3번째 동종계 브랜드를 빼고 판정해 그 값만 있으면 섹션이 숨었음 */
const COMMISSION_KEYS: StoreOpenInputKey[] = [
  'operationalState',
  'marginInfo1',
  'marginInfo2',
  'subBrandName1',
  'subBrandMargin1',
  'subBrandName2',
  'subBrandMargin2',
  'subBrandName3',
  'subBrandMargin3',
  'payState',
  'etcInfo',
];

const RENT_ROWS: [StoreOpenInputKey, string][] = [
  ['rentContractPeriod', '계약기간'],
  ['rentDepositAmount', '보증금'],
  ['rentMonthlyAmount', '임차료'],
  ['rentMaintenanceFee', '관리비'],
  ['rentPaymentDay', '납부일'],
  ['rentEtcNote', '기타사항'],
];

const PROMOTION_ROWS: [StoreOpenInputKey, string][] = [
  ['promoSms', 'SMS발송'],
  ['promoGift', '사은품'],
  ['promoDiscount', '금액할인권'],
  ['promoDoubleMileage', '더블마일리지적립'],
  ['promoForeignerEvent', '외국인고객대상이벤트'],
  ['promoEtc', '기타사항'],
];

type Props = {
  renderCell: (key: StoreOpenCellKey) => ReactNode;
  /** 작성 화면 — 셀을 입력칸으로 채움, 섹션은 값이 없어도 모두 보임 */
  editable?: boolean;
  /** 매장 형태 C — 임대차·프로모션 섹션 */
  isDirectShop: boolean;
  isNoInterior: boolean;
  /** 조회 화면 섹션 노출 판정 — 레거시는 값이 하나도 없으면 섹션을 숨김 */
  hasValue?: (keys: StoreOpenInputKey[]) => boolean;
  onShowDetail?: () => void;
};

/** (구)매장 OPEN 품의(8) 백화점·직영점 본문 표 — 작성·조회 공용, 레거시와 같은 표 구성 */
const StoreOpenTables = ({
  renderCell,
  editable = false,
  isDirectShop,
  isNoInterior,
  hasValue = () => true,
  onShowDetail,
}: Props) => {
  const { t } = useTranslation();

  const cell = (
    key: StoreOpenCellKey,
    { className, ...props }: { className?: string; rowSpan?: number } = {},
  ) => {
    const content = renderCell(key);
    return (
      <td
        className={classNames(
          className ?? (editable ? undefined : 'align-center'),
          // 작성 화면의 입력칸 아닌 값(서버 값)은 조회 화면과 같은 여백
          editable && typeof content !== 'object' && 'is-readonly',
        )}
        {...props}
      >
        {content}
      </td>
    );
  };

  const showCommission = editable || hasValue(COMMISSION_KEYS);
  const showRent =
    isDirectShop && (editable || hasValue(RENT_ROWS.map(([key]) => key)));
  const showPromotion =
    isDirectShop && (editable || hasValue(PROMOTION_ROWS.map(([key]) => key)));
  const showDetailButton = !isNoInterior && !!onShowDetail;

  const labelRows = (rows: [StoreOpenInputKey, string][]) => (
    <ApprovalFormTable editable={editable}>
      <colgroup>
        <col style={{ width: '25%' }} />
        <col />
      </colgroup>
      <tbody>
        {rows.map(([key, label]) => (
          <tr key={key}>
            <th>{t(label)}</th>
            {cell(key)}
          </tr>
        ))}
      </tbody>
    </ApprovalFormTable>
  );

  return (
    <StoreOpenRoot>
      <InfoSection title={editable ? t('매장현황') : t('기본정보')}>
        <ApprovalFormTable editable={editable}>
          <thead>
            <tr>
              <th>{t('브랜드')}</th>
              <th>{t('매장명(지역)')}</th>
              <th>{t('오픈일')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              {cell('brand', { className: 'align-center' })}
              {cell('shopName', { className: 'align-center' })}
              {cell('openDate', { className: 'align-center' })}
            </tr>
          </tbody>
        </ApprovalFormTable>
      </InfoSection>

      <InfoSection>
        <SectionHeader>
          <InfoSectionTitle>{t('상세내역')}</InfoSectionTitle>
          <ApprovalTableUnit>{t('(단위 : 천 원)')}</ApprovalTableUnit>
        </SectionHeader>
        <ApprovalFormTable editable={editable}>
          <tbody>
            <tr>
              <th rowSpan={3}>{t('매출목표')}</th>
              <th colSpan={2}>S/S</th>
              <th colSpan={2}>F/W</th>
              <th colSpan={2}>TOTAL</th>
            </tr>
            <tr>
              <th>{t('시즌목표')}</th>
              <th>{t('월평균')}</th>
              <th>{t('시즌목표')}</th>
              <th>{t('월평균')}</th>
              <th>{t('목표')}</th>
              <th>{t('월평균')}</th>
            </tr>
            <tr>
              {cell('ssSeasonGoalAmount')}
              {cell('ssMonthlyAvgAmount')}
              {cell('fwSeasonGoalAmount')}
              {cell('fwMonthlyAvgAmount')}
              {/* 레거시 문서 호환 — 레거시도 「목표」 칸 값을 totalMonthlyAvgAmount 로 저장 */}
              {cell('totalMonthlyAvgAmount')}
              {cell('totalAnnualGoalAmount')}
            </tr>
            <tr>
              <th rowSpan={2}>{t('매장인원')}</th>
              <th>SM</th>
              <th>{t('FA(명)')}</th>
              <th>{t('최종근무지')}</th>
              <th>{t('경력')}</th>
              <th>{t('월 예상 수수료')}</th>
              <th>{t('수수료(%)')}</th>
            </tr>
            <tr>
              {cell('shopMasterName')}
              {cell('faCount')}
              {cell('lastWorkplace')}
              {cell('careerYears')}
              {cell('monthlyFee')}
              {cell('commissionType')}
            </tr>
          </tbody>
        </ApprovalFormTable>
      </InfoSection>

      {showCommission && (
        <InfoSection
          title={
            editable && isDirectShop
              ? `${t('점수수료 현황')}${t('(mall 입점시)')}`
              : t('점수수료 현황')
          }
        >
          <ApprovalFormTable editable={editable}>
            <tbody>
              <tr>
                <th rowSpan={2}>{t('운영형태')}</th>
                <th colSpan={2}>{t('백화점마진')}</th>
                <th colSpan={2}>{t('동종계마진')}</th>
                <th rowSpan={2}>{t('결재조건')}</th>
                <th rowSpan={2}>{t('비고')}</th>
              </tr>
              <tr>
                <th>{t('정상(%)')}</th>
                <th>{t('행사(%)')}</th>
                <th>{t('브랜드명')}</th>
                <th>{t('마진')}</th>
              </tr>
              <tr>
                {cell('operationalState', { rowSpan: 3 })}
                {cell('marginInfo1', { rowSpan: 3 })}
                {cell('marginInfo2', { rowSpan: 3 })}
                {cell('subBrandName1')}
                {cell('subBrandMargin1')}
                {cell('payState', { rowSpan: 3 })}
                {cell('etcInfo', { rowSpan: 3 })}
              </tr>
              <tr>
                {cell('subBrandName2')}
                {cell('subBrandMargin2')}
              </tr>
              <tr>
                {cell('subBrandName3')}
                {cell('subBrandMargin3')}
              </tr>
            </tbody>
          </ApprovalFormTable>
        </InfoSection>
      )}

      <InfoSection title={t('인테리어 정보')}>
        <ApprovalFormTable editable={editable}>
          <thead>
            <tr>
              <th>{t('평수')}</th>
              <th>{t('형태')}</th>
              <th>{t('공사구분')}</th>
              <th>{t('인테리어업체')}</th>
              <th>{t('예상비용')}</th>
              <th>{t('기타비용(집기)')}</th>
              <th>{t('비고')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              {cell('shopArea', { rowSpan: 2, className: 'align-center' })}
              {cell('shopForm', { rowSpan: 2, className: 'align-center' })}
              {cell('constructionType', {
                rowSpan: 2,
                className: 'align-center',
              })}
              {cell('interiorCompany', {
                rowSpan: 2,
                className: 'align-center',
              })}
              {cell('interiorCost', {
                rowSpan: showDetailButton ? 1 : 2,
                className: 'align-right',
              })}
              {cell('shopEtcCost', { rowSpan: 2 })}
              {cell('interiorNote', { rowSpan: 2 })}
            </tr>
            <tr>
              {showDetailButton && (
                <td className="align-center is-readonly">
                  <BoxButton
                    type="button"
                    variant="secondary"
                    size="small"
                    onClick={onShowDetail}
                  >
                    {t('상세보기')}
                  </BoxButton>
                </td>
              )}
            </tr>
          </tbody>
        </ApprovalFormTable>
      </InfoSection>

      {showRent && (
        <InfoSection title={t('임대차 계약사항')}>
          {labelRows(RENT_ROWS)}
        </InfoSection>
      )}

      {showPromotion && (
        <InfoSection title={t('프로모션')}>
          {labelRows(PROMOTION_ROWS)}
        </InfoSection>
      )}

      <InfoSection title={t('협조사항')}>
        <ApprovalFormTable editable={editable}>
          <colgroup>
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
            <col />
          </colgroup>
          <tbody>
            <tr>
              <th rowSpan={3}>{t('업무협조')}</th>
              <th>{t('영업관리')}</th>
              {cell('coopSalesMgmt')}
            </tr>
            <tr>
              <th>{t('경영정보')}</th>
              {cell('coopMgmtInfo')}
            </tr>
            <tr>
              <th>{t('마케팅')}</th>
              {cell('coopMarketing')}
            </tr>
            <tr>
              <th colSpan={2}>{t('본사지원사항')}</th>
              {cell('headquartersSupport')}
            </tr>
            <tr>
              <th colSpan={2}>{t('기타사항')}</th>
              {cell('operationNote')}
            </tr>
          </tbody>
        </ApprovalFormTable>
      </InfoSection>
    </StoreOpenRoot>
  );
};

export default StoreOpenTables;
